import { useNavigate } from 'react-router-dom'
import { useAuth as useClerkAuth } from '@clerk/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useAuthStore } from '@/features/auth/authStore.ts'
import { useNotificationDrawerStore } from '@/features/notification/store'
import { UserStatus } from '@/schema'
import { ROUTES } from '@/utils/routes'

import { authApi } from '../features/auth/authApi'
import { STATUS_CODES } from '../utils/constants'

const AUTH_QUERY_KEY = ['auth', 'user']

const readPersistedToken = () => {
  try {
    return localStorage.getItem('token')
  } catch {
    return null
  }
}

type UseCurrentUserOptions = {
  enabled?: boolean
}

export const useLogout = (options?: { redirectTo?: string }) => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const setToken = useAuthStore((s) => s.setToken)
  const { signOut } = useClerkAuth()

  return useMutation({
    mutationFn: async () => {
      // Clear auth state
      setToken(null)

      // Clear all auth-related storage
      localStorage.removeItem('token')
      await signOut()

      // Clear all cached data
      queryClient.clear()

      // Reset notification store state
      useNotificationDrawerStore.getState().reset()

      // Navigate to login (or custom redirect) synchronously after full cleanup guarantees
      const redirectPath = options?.redirectTo || ROUTES.LOGIN
      navigate(redirectPath, { replace: true })
    },
    onSuccess: () => {
      // Intentionally left blank. Navigation moved to mutation execution.
    },
  })
}

/**
 * Hook to get current user data
 * Automatically activates pending users
 */
export const useCurrentUser = (options: UseCurrentUserOptions = {}) => {
  const storeToken = useAuthStore((s) => s.token)
  const persistedToken = readPersistedToken()
  const hasToken = !!persistedToken || !!storeToken
  const shouldEnable = (options.enabled ?? true) && hasToken

  return useQuery({
    queryKey: AUTH_QUERY_KEY,
    queryFn: async () => {
      try {
        let user = await authApi.getCurrentUser()

        // If user status is pending, activate the user
        if (user.status === UserStatus.PENDING) {
          user = await authApi.activateUser()
        }
        // update roles in store
        useAuthStore.getState().setRoles(user)
        return user
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        if (error.response?.status === STATUS_CODES.FORBIDDEN) {
          // Dispatch a custom event to let the AuthProvider handle Clerk logout and navigation
          globalThis.dispatchEvent(new Event('auth:logout'))
        }
        throw error
      }
    },
    enabled: shouldEnable,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
  })
}

/**
 * Hook to check if user is authenticated
 */
export const useIsAuthenticated = () => {
  const storeToken = useAuthStore((s) => s.token)
  const hasToken = !!localStorage.getItem('token') || !!storeToken
  const { data: user, isLoading } = useCurrentUser()

  // If no token, not authenticated
  if (!hasToken) {
    return false
  }

  // If we have a token and data loaded, check if user exists
  if (!isLoading && hasToken) {
    return !!user
  }

  // If still loading but we have a token, assume authenticated temporarily
  // This prevents flash of login page during initial load
  return hasToken
}
